import React, { useState, useEffect } from "react";
import { InterviewRound } from "../../../../Dto/GenerateJobDescription.dto";
import {
  Button,
  Divider,
  Flex,
  Modal,
  Tag,
  Typography,
  Spin,
  message,
  Empty,
} from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { SampleQuestion } from "../../../../Interface";
import { Requirements } from "../../../../Entities/JobDescription";
import { useSampleQuestions } from "../../../../Context/SampleQuestionsContext";

interface InterviewRoundOverviewModalProps {
  showroundOverviewModal: boolean;
  handleClose: () => void;
  round: InterviewRound;
  requirements: Requirements[];
}

const InterviewRoundOverviewModal: React.FC<
  InterviewRoundOverviewModalProps
> = ({ handleClose, round, showroundOverviewModal, requirements }) => {
  const [showSampleQuestions, setShowSampleQuestions] = useState(false);
  const [sampleQuestions, setSampleQuestions] = useState<SampleQuestion[]>([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const { getSampleQuestionsByRound } = useSampleQuestions();

  const fetchSampleQuestions = async () => {
    if (!round?._id) return;

    setIsLoadingQuestions(true);
    try {
      const questions = await getSampleQuestionsByRound(round, requirements);
      setSampleQuestions(questions || []);
    } catch (error: any) {
      console.error("Error fetching sample questions:", error);
      // Show user-friendly error message
      if (error?.response?.status === 500) {
        message.error("Unable to generate sample questions. Please try again later.");
      } else if (error?.response?.status) {
        message.error("Failed to load sample questions. Please try again.");
      } else {
        message.error("An error occurred while fetching sample questions.");
      }
      setSampleQuestions([]);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // Reset questions when round changes or requirements change
  useEffect(() => {
    setShowSampleQuestions(false);
    setSampleQuestions([]);
  }, [round?._id, requirements]);

  const handleViewQuestions = () => {
    if (showSampleQuestions) {
      setShowSampleQuestions(false);
    } else {
      setShowSampleQuestions(true);
      if (sampleQuestions.length === 0) {
        fetchSampleQuestions();
      }
    }
  };

  const renderQuestion = (question: SampleQuestion, index: number) => {
    return (
      <div key={question.id} style={{ marginBottom: 16 }}>
        <Typography.Text strong>
          {index + 1}. {question.question}
        </Typography.Text>

        {question.answerType === "mcq" && (
          <div style={{ marginTop: 8 }}>
            <Tag color="blue">Multiple Choice Question</Tag>
          </div>
        )}

        {question.answerType === "descriptive" && (
          <div style={{ marginTop: 8 }}>
            <Tag color="blue">Descriptive Answer Required</Tag>
          </div>
        )}

        {question.answerType === "coding" && (
          <div style={{ marginTop: 8 }}>
            <Tag color="purple">Coding Answer Required</Tag>
          </div>
        )}
      </div>
    );
  };
  return (
    <Modal
      maskClosable={false}
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          Round Details
        </Typography.Title>
      }
      open={showroundOverviewModal}
      onCancel={handleClose}
      footer={
        <Flex justify="space-between" gap={8}>
          <Button
            type="primary"
            icon={
              showSampleQuestions ? <EyeInvisibleOutlined /> : <EyeOutlined />
            }
            onClick={handleViewQuestions}
            disabled={!round?._id}
            style={{ minWidth: 140 }}
          >
            {showSampleQuestions ? "Hide Questions" : "View Sample Questions"}
          </Button>
          <Button
            icon={<CloseOutlined />}
            onClick={handleClose}
            style={{ minWidth: 100 }}
          >
            Close
          </Button>
        </Flex>
      }
      width={600}
      centered
      style={{
        maxHeight: "600px",
      }}
      styles={{
        body: {
          maxHeight: "500px",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        },
      }}
    >
      <Flex vertical gap={16}>
        <Divider style={{ margin: 0 }} />
        <Flex vertical gap={4}>
          <Typography.Text strong>Title</Typography.Text>
          <Typography.Text>{round?.title || 'Untitled Round'}</Typography.Text>
        </Flex>

        <Flex vertical gap={4}>
          <Typography.Text strong>Category</Typography.Text>
          <Flex gap={4} wrap>
            {(round?.category && round.category.length > 0) ? (
              round.category.map((category, index) => {
                const colorList = [
                  "magenta",
                  "red",
                  "volcano",
                  "orange",
                  "gold",
                  "lime",
                  "green",
                  "cyan",
                  "blue",
                  "geekblue",
                  "purple",
                ];
                const color = colorList[index % colorList.length];

                return (
                  <Tag
                    key={category}
                    color={color}
                    style={{
                      width: "fit-content",
                    }}
                  >
                    {category || "Custom"}
                  </Tag>
                );
              })
            ) : (
              <Typography.Text type="secondary">No category specified</Typography.Text>
            )}
          </Flex>
        </Flex>

        <Flex vertical gap={4}>
          <Typography.Text strong>Answer Type</Typography.Text>
          <Tag
            color={
              round?.answerType?.toLowerCase() === "mcq" ? "blue" : "green"
            }
            style={{
              width: "fit-content",
            }}
          >
            {round?.answerType?.toUpperCase() || "DESCRIPTIVE"}
          </Tag>
        </Flex>

        <Flex gap={24}>
          <Flex vertical gap={4}>
            <Typography.Text strong>Pass Percentage</Typography.Text>
            <Tag
              color="blue"
              style={{ width: "fit-content" }}
            >
              {round?.passPercentage ?? 60}%
            </Tag>
          </Flex>

          <Flex vertical gap={4}>
            <Typography.Text strong>Must Qualify</Typography.Text>
            <Tag
              color={round?.isMustQualify !== false ? "green" : "orange"}
              style={{ width: "fit-content" }}
            >
              {round?.isMustQualify !== false ? "Yes" : "No"}
            </Tag>
          </Flex>
        </Flex>

        <Flex vertical gap={4}>
          <Typography.Text strong>Topics of Assessment</Typography.Text>
          <Typography.Text>
            {round?.questionGenerationPrompt || 'No topics specified'}
          </Typography.Text>
        </Flex>

        {round?.evaluationPrompt && (
          <Flex vertical gap={4}>
            <Typography.Text strong>Evaluation Criteria</Typography.Text>
            <Typography.Text>{round.evaluationPrompt}</Typography.Text>
          </Flex>
        )}

        {showSampleQuestions && (
          <>
            <Divider />
            <Flex vertical gap={4}>
              <Typography.Text strong>Sample Questions</Typography.Text>

              {isLoadingQuestions ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 8 }}>
                    <Typography.Text type="secondary">
                      Loading sample questions...
                    </Typography.Text>
                  </div>
                </div>
              ) : sampleQuestions.length > 0 ? (
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px",
                    border: "1px solid #f0f0f0",
                    borderRadius: "6px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  {sampleQuestions.map((question, index) =>
                    renderQuestion(question, index)
                  )}
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  <Empty description="No sample questions available for this round.">
                  </Empty>
                </div>
              )}
            </Flex>
          </>
        )}
      </Flex>
    </Modal>
  );
};

export default InterviewRoundOverviewModal;
